import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  STUDIES_ADD_CONDITIONS_STARTED_EVENT,
  STUDIES_ADD_CONDITIONS_ENDED_EVENT,
  composeStudiesAddConditionsEventMetadata,
  ExecutionType,
} from 'performance/processStudiesEvents';
import {
  getNewConditionCount,
  getNewConditionReplicateCount,
  updateProcedureRun,
  UpdateProcedureRunParams,
} from 'procedures/network/ProcedureRunDAO';
import {OnUnknownErrorAction} from 'studies/sidepanel/CreationTrayTask';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import {withLoadingScreen} from 'util/Promises';

import {showConditionCreationTrayTask} from './ConditionCreationTrayTask';

export async function onAddConditions(
  studyId: string,
  procedureType: ProcedureType,
  studyType: StudyType,
  update: UpdateProcedureRunParams,
  callbacks: {
    onSuccess: () => void;
    onOpen: () => void;
    onUnknownError: OnUnknownErrorAction;
  }
): Promise<void> {
  const numNewConditions = getNewConditionCount(update);
  logProfilerTimingEvent(STUDIES_ADD_CONDITIONS_STARTED_EVENT);
  const initResponse = await withLoadingScreen(
    updateProcedureRun(update, {supportsAsync: true}),
    `Adding ${getProcedureRunConditionTextForProcedureType(procedureType, {
      plural: numNewConditions > 1,
    })}...`
  );
  if (!initResponse.async) {
    callbacks.onSuccess();
    callbacks.onOpen();
    logProfilerTimingEvent(
      STUDIES_ADD_CONDITIONS_ENDED_EVENT,
      composeStudiesAddConditionsEventMetadata(studyType, ExecutionType.SYNC)
    );
    return;
  }

  showConditionCreationTrayTask(
    initResponse.data.taskId,
    initResponse.data.status_url,
    initResponse.taskMeta,
    [
      {
        id: update.id,
        studyId,
        procedureType,
        studyType,
        numNewConditions,
      },
    ],
    {
      onSuccess: (response) => {
        callbacks.onSuccess();
        logProfilerTimingEvent(
          STUDIES_ADD_CONDITIONS_ENDED_EVENT,
          composeStudiesAddConditionsEventMetadata(studyType, ExecutionType.ASYNC)
        );
      },
      onOpen: callbacks.onOpen,
      onUnknownError: callbacks.onUnknownError,
    },
    initResponse.data.timingMeta
  );
}

/**
 * General procedure run update handler used by the split-pool design flow. Unlike onAddConditions, it
 * keeps non-condition updates (e.g. drawn edges) synchronous, since the backend only runs async tasks
 * for condition creation. Will grow to cover deleting conditions and edges.
 */
export async function onUpdateProcedureRun(
  studyId: string,
  procedureType: ProcedureType,
  studyType: StudyType,
  // The number of active + downstream unit operations
  numCoveredUnitOperations: number,
  update: UpdateProcedureRunParams,
  callbacks: {
    onSuccess: () => void;
    onOpen: () => void;
    onUnknownError: OnUnknownErrorAction;
  }
): Promise<void> {
  // Only condition creation runs async, so edge-only updates stay synchronous. The shared helper
  // counts new conditions across both condition fields.
  const numNewConditions = getNewConditionCount(update);
  const hasNewConditions = numNewConditions > 0;
  const initResponse = await withLoadingScreen(
    updateProcedureRun(update, {supportsAsync: hasNewConditions}),
    'Saving changes...'
  );
  if (!initResponse.async) {
    callbacks.onSuccess();
    callbacks.onOpen();
    return;
  }

  showConditionCreationTrayTask(
    initResponse.data.taskId,
    initResponse.data.status_url,
    initResponse.taskMeta,
    [
      {
        id: update.id,
        studyId,
        procedureType,
        studyType,
        numNewConditions,
        numNewConditionReplicates: getNewConditionReplicateCount(update),
        numCoveredUnitOperations,
      },
    ],
    {
      onSuccess: () => {
        callbacks.onSuccess();
      },
      onOpen: callbacks.onOpen,
      onUnknownError: callbacks.onUnknownError,
    },
    initResponse.data.timingMeta
  );
}
